<template>
  <transition name="alert-fade">
    <div v-if="show" class="base-alert-overlay" @click.self="onDismiss">
      <div
        class="base-alert"
        role="alertdialog"
        aria-modal="true"
        :aria-labelledby="`${dialogId}-title`"
        :aria-describedby="`${dialogId}-body`">
        <div class="base-alert__header">
          <span class="base-alert__icon" :class="`base-alert__icon--${variant}`">
            <i class="tim-icons" :class="resolvedIcon"></i>
          </span>
          <h4 :id="`${dialogId}-title`" class="base-alert__title">{{ title }}</h4>
        </div>

        <div :id="`${dialogId}-body`" class="base-alert__body">
          <slot>{{ message }}</slot>
        </div>

        <div class="base-alert__actions">
          <slot name="actions">
            <base-button
              v-if="showCancel"
              ref="cancelButton"
              type="secondary"
              size="sm"
              @click="onCancel">
              {{ cancelText }}
            </base-button>
            <base-button
              ref="confirmButton"
              :type="confirmType"
              size="sm"
              :disabled="loading"
              @click="onConfirm">
              {{ confirmText }}
            </base-button>
          </slot>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
// House alert and confirm dialog. Replaces sweetalert2 so dialogs are styled by
// the same primitives as the rest of the app, respond to light mode, and size
// to their own content instead of a fixed vendor width.
//
// Alert:   <base-alert-dialog :show="x" title="..." message="..." @confirm="..." />
// Confirm: add show-cancel, confirm-text, cancel-text and handle @cancel.
const VARIANT_ICONS = {
  warning: 'icon-alert-circle-exc',
  danger: 'icon-alert-circle-exc',
  success: 'icon-check-2',
  info: 'icon-bulb-63',
  primary: 'icon-bulb-63',
}

export default {
  name: 'base-alert-dialog',
  props: {
    show: { type: Boolean, default: false },
    title: { type: String, default: '' },
    // Plain body copy. For anything richer, use the default slot instead.
    message: { type: String, default: '' },
    variant: {
      type: String,
      default: 'warning',
      validator: v => ['warning', 'danger', 'success', 'info', 'primary'].includes(v),
    },
    // tim-icons class without the `tim-icons` prefix. Defaults per variant.
    icon: { type: String, default: '' },
    confirmText: { type: String, default: 'OK' },
    cancelText: { type: String, default: 'Cancel' },
    // Off by default so the plain alert case stays a single acknowledge button.
    showCancel: { type: Boolean, default: false },
    // Danger tones the confirm button red for destructive confirms.
    confirmVariant: {
      type: String,
      default: 'primary',
      validator: v => ['primary', 'danger'].includes(v),
    },
    loading: { type: Boolean, default: false },
    // When false, ESC and overlay clicks do not dismiss.
    dismissible: { type: Boolean, default: true },
  },
  data() {
    return {
      previousBodyOverflow: '',
      scrollLocked: false,
      dialogId: `base-alert-${this._uid}`,
    }
  },
  computed: {
    resolvedIcon() {
      return this.icon ? `tim-icons ${this.icon}` : `tim-icons ${VARIANT_ICONS[this.variant]}`
    },
    confirmType() {
      return this.confirmVariant === 'danger' ? 'danger' : 'primary'
    },
  },
  watch: {
    // Immediate so a dialog rendered with show already true still locks scroll
    // and listens for ESC.
    show: {
      immediate: true,
      handler(val) {
        if (val) {
          this.lockScroll()
          document.addEventListener('keydown', this.onKeydown, true)
          this.$nextTick(this.focusInitialButton)
        } else {
          this.unlockScroll()
          document.removeEventListener('keydown', this.onKeydown, true)
        }
      },
    },
  },
  beforeDestroy() {
    this.unlockScroll()
    document.removeEventListener('keydown', this.onKeydown, true)
  },
  methods: {
    // The dialog can open on top of a drawer that already locked the body, so
    // the previous value is restored rather than blanked. Blanking it would let
    // the page behind an open drawer scroll again once the alert closed.
    lockScroll() {
      if (this.scrollLocked) return
      this.previousBodyOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      this.scrollLocked = true
    },

    unlockScroll() {
      if (!this.scrollLocked) return
      document.body.style.overflow = this.previousBodyOverflow || ''
      this.scrollLocked = false
    },

    // Destructive confirms open focused on Cancel so Enter cannot fire the
    // destructive action; everything else focuses its single acknowledge button.
    focusInitialButton() {
      const target = this.confirmVariant === 'danger' && this.showCancel
        ? this.$refs.cancelButton
        : this.$refs.confirmButton
      if (target && target.$el && typeof target.$el.focus === 'function') {
        target.$el.focus()
      }
    },

    // Capture phase plus stopPropagation: ESC closes this dialog only, never
    // the drawer or page under it that also listens for ESC on document.
    onKeydown(event) {
      if (event.key === 'Escape') {
        event.stopPropagation()
        this.onDismiss()
      }
    },
    onDismiss() {
      if (!this.dismissible) return
      this.onCancel()
    },
    onCancel() {
      this.$emit('cancel')
      this.$emit('close')
    },
    onConfirm() {
      if (this.loading) return
      this.$emit('confirm')
    },
  },
}
</script>

<style lang="scss" scoped>
.base-alert-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.55);
  // Above drawers (1051) and Element UI dialogs (2000+) so a confirm raised
  // from inside either is never trapped behind it.
  z-index: 3000;
}

.base-alert {
  // Sized to its own content, capped so body copy keeps a readable measure and
  // never exceeds a small viewport.
  width: auto;
  // The floor keeps short notices from collapsing to their text; capped at 100%
  // so it never forces the dialog wider than a small viewport.
  min-width: min(360px, 100%);
  max-width: min(520px, 100%);
  // 100dvh accounts for mobile browser chrome; 100vh is the fallback for
  // browsers without dvh. Long copy scrolls inside the dialog rather than
  // pushing its buttons off screen.
  max-height: calc(100vh - 32px);
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  padding: 24px;
  background: #22233a;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.4285rem;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.4), 0 5px 15px rgba(0, 0, 0, 0.2);
}

.base-alert__header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.base-alert__icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  font-size: 16px;
}

.base-alert__icon--warning {
  background: rgba(255, 141, 114, 0.15);
  color: #ff8d72;
}
.base-alert__icon--danger {
  background: rgba(253, 93, 147, 0.15);
  color: #fd5d93;
}
.base-alert__icon--success {
  background: rgba(0, 242, 195, 0.15);
  color: #00f2c3;
}
.base-alert__icon--info {
  background: rgba(29, 140, 248, 0.15);
  color: #1d8cf8;
}
.base-alert__icon--primary {
  background: rgba(225, 78, 202, 0.15);
  color: #e14eca;
}

.base-alert__title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #ffffff;
}

.base-alert__body {
  // Holds the copy to a comfortable line length while letting a wide action row
  // set the dialog width.
  max-width: 46ch;
  font-size: 0.875rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.7);
  overflow-wrap: anywhere;
}

.base-alert__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  // Negative side margins pull the rule out to the dialog edges, past the
  // body padding.
  margin: 20px -24px -24px;
  padding: 16px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);

  // The theme gives every .btn a 4px vertical margin, which stacked on top of
  // this row's padding. `gap` handles the spacing between buttons instead.
  ::v-deep .btn {
    margin: 0;
  }

  // Thumb-sized targets once the row is narrow enough that the buttons would
  // otherwise wrap into a cramped stack.
  @media (max-width: 480px) {
    ::v-deep .btn {
      flex: 1 1 100%;
    }
  }
}

.alert-fade-enter-active,
.alert-fade-leave-active {
  transition: opacity 0.2s ease;
}
.alert-fade-enter,
.alert-fade-leave-to {
  opacity: 0;
}

.white-content .base-alert {
  background: #ffffff;
  border-color: #e3e3e3;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1), 0 4px 12px rgba(0, 0, 0, 0.06);
}

.white-content .base-alert__actions {
  border-top-color: #e3e3e3;
}

.white-content .base-alert__title {
  color: #344675;
}

.white-content .base-alert__body {
  color: #66615b;
}
</style>
